/* =====================================================================
   HL LONDON — ANIMATED SUGGESTED SEARCH (Phase 3, site-wide)

   Types a curated search term into the search field's PLACEHOLDER, holds,
   deletes it, and moves to the next — a slow loop, Oh Polly's cue slowed
   down for a luxury tone.

   WHAT IT DOES NOT TOUCH
   The input's value, name, form, or any listener on it. Only the
   `placeholder` attribute is written, so live search, predictive search
   and the snize/app integrations are untouched by construction: none of
   them read the placeholder.

   PAUSE RULES (all of them required by the brief, and each is a real
   usability point, not decoration):
     - focus            ONLY when the shopper put the caret there themselves
                        (a tap or click on the field, or a key pressed in
                        it). Both search overlays on this theme call
                        input.focus() 300-500ms after opening, and pausing
                        on THAT focus was the "types three characters then
                        snaps back to Search..." bug (Sept 2026): the term
                        got 4-5 characters in, the programmatic focus
                        landed, and the field sat static for the rest of
                        the visit. A programmatic focus now leaves the
                        animation running; a real one still pauses it.
     - hover            they are about to click
     - field has value  the placeholder is not visible anyway, and
                        restoring it later must not clobber their text
     - tab hidden       no point animating to nobody; also stops a
                        background tab burning timer wakeups
     - reduced motion   one static placeholder, no animation at all

   Timings come straight from the brief: ~70ms per character typing,
   ~1.4s hold, ~40ms per character deleting, ~500ms between terms.
   ===================================================================== */
(function () {
  'use strict';

  var TYPE_MS = 70;
  var HOLD_MS = 1400;
  var DEL_MS = 40;
  var GAP_MS = 500;

  /* Fallback list. The header section supplies the real one via
     data-hl-search-suggest so it is editable without a code change. */
  var DEFAULT_TERMS = [
    'luxury fur coat',
    'faux fur gilet',
    'cowboy boots',
    'co-ord',
    'new in',
    "kids' fur"
  ];

  function readTerms() {
    var host = document.querySelector('[data-hl-search-suggest]');
    if (host) {
      var raw = host.getAttribute('data-hl-search-suggest') || '';
      var list = raw.split('|')
        .map(function (s) { return s.trim(); })
        .filter(function (s) { return s.length > 0; });
      if (list.length) return list;
    }
    return DEFAULT_TERMS;
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function Typer(input, terms) {
    this.input = input;
    this.terms = terms;
    this.base = input.getAttribute('placeholder') || '';
    this.i = 0;
    this.timer = null;
    this.paused = false;
    this.hovered = false;
    this.focused = false;
    /* Timestamp of the last pointer/keyboard contact with the field. A
       focus event that lands within USER_FOCUS_MS of it is the shopper's;
       one that lands with no contact at all is a script's. */
    this.touchedAt = 0;
  }
  var USER_FOCUS_MS = 800;

  Typer.prototype.write = function (text) {
    /* One attribute write, guarded — the input is inside a container that
       other scripts observe, so an unconditional write is churn. */
    if (this.input.getAttribute('placeholder') !== text) {
      this.input.setAttribute('placeholder', text);
    }
  };

  Typer.prototype.stop = function () {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
  };

  /* True whenever the animation must not run.

     The visibility test matters more than it looks: on this theme the
     header search is an OVERLAY, so its input exists in the DOM on every
     page but is display:none until someone opens search. Without this
     check the typewriter ran continuously against a field nobody could
     see — timers and attribute writes for no one — and, because focus()
     does not fire on a hidden input, the focus pause could not engage
     either.

     MEASURED on this theme: the closed overlay's input computes
       display: inline-block   visibility: hidden   offsetParent: <form>
     i.e. it is hidden with VISIBILITY, not display. So an offsetParent
     test alone returns "visible" and misses it — and because a
     visibility:hidden input is not focusable, focus() fires nothing there
     either. Both conditions are checked. */
  Typer.prototype.visible = function () {
    if (this.input.offsetParent === null) return false;
    var cs = window.getComputedStyle(this.input);
    return cs.visibility !== 'hidden' && cs.display !== 'none';
  };

  Typer.prototype.blocked = function () {
    return this.focused ||
      this.hovered ||
      document.hidden ||
      !this.visible() ||
      (this.input.value || '').length > 0;
  };

  Typer.prototype.at = function (fn, ms) {
    var self = this;
    this.stop();
    this.timer = setTimeout(function () { fn.call(self); }, ms);
  };

  /* While blocked, show the plain placeholder and poll gently until the
     field is free again. A 400ms poll costs nothing and avoids wiring a
     listener for every possible cause (value set by an app, tab focus,
     autofill). */
  Typer.prototype.idle = function () {
    this.write(this.base);
    this.at(this.start, 400);
  };

  Typer.prototype.start = function () {
    if (this.blocked()) return this.idle();
    this.term = this.terms[this.i % this.terms.length];
    this.n = 0;
    this.typeIn();
  };

  Typer.prototype.typeIn = function () {
    if (this.blocked()) return this.idle();
    this.n += 1;
    this.write(this.term.slice(0, this.n));
    if (this.n >= this.term.length) {
      this.at(this.typeOut, HOLD_MS);
    } else {
      this.at(this.typeIn, TYPE_MS);
    }
  };

  Typer.prototype.typeOut = function () {
    if (this.blocked()) return this.idle();
    this.n -= 1;
    this.write(this.term.slice(0, Math.max(0, this.n)));
    if (this.n <= 0) {
      this.i += 1;
      this.at(this.start, GAP_MS);
    } else {
      this.at(this.typeOut, DEL_MS);
    }
  };

  Typer.prototype.bind = function () {
    var self = this;
    var el = this.input;

    var touch = function () { self.touchedAt = Date.now(); };
    el.addEventListener('pointerdown', touch, { passive: true });
    el.addEventListener('touchstart', touch, { passive: true });
    el.addEventListener('mousedown', touch, { passive: true });
    el.addEventListener('keydown', function () {
      /* A key pressed in the field is intent even if the focus was
         programmatic: pause now rather than waiting for the value. */
      self.touchedAt = Date.now();
      self.focused = true;
      self.stop();
      self.write(self.base);
    });
    el.addEventListener('focus', function () {
      if (Date.now() - self.touchedAt > USER_FOCUS_MS) return;   /* script focus: keep going */
      self.focused = true;
      self.stop();
      self.write(self.base);
    });
    el.addEventListener('blur', function () {
      self.focused = false;
      self.at(self.start, GAP_MS);
    });
    el.addEventListener('mouseenter', function () {
      /* Touch screens synthesise mouseenter on a tap and never send the
         matching mouseleave, which would leave the field paused for the
         rest of the visit. Hover is a mouse concept: only honour it there. */
      if (window.matchMedia && !window.matchMedia('(hover: hover)').matches) return;
      self.hovered = true;
      self.stop();
      self.write(self.base);
    });
    el.addEventListener('mouseleave', function () {
      self.hovered = false;
      if (!self.focused) self.at(self.start, GAP_MS);
    });
    /* Typing into the field, or an app writing into it, both land here. */
    el.addEventListener('input', function () {
      if ((el.value || '').length > 0) {
        self.stop();
        self.write(self.base);
      }
    });

    el.classList.add('hl-search-suggesting');
    this.at(this.start, 900);   /* let the page settle before it starts */
  };

  function fields() {
    /* Every search field on the page: the header overlay, the /search page
       form, and any drawer variant. Keyed on name="q" so it cannot pick up
       an unrelated text input. */
    var out = [];
    var nodes = document.querySelectorAll('input[name="q"]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var t = (el.getAttribute('type') || 'text').toLowerCase();
      if (t !== 'text' && t !== 'search') continue;
      if (el.hasAttribute('data-hl-suggest-done')) continue;
      el.setAttribute('data-hl-suggest-done', '1');
      out.push(el);
    }
    return out;
  }

  function boot() {
    var terms = readTerms();
    if (!terms.length) return;

    var els = fields();

    if (reducedMotion()) {
      /* One static suggestion, no animation — still a cue, just a still
         one. The brief asks for exactly this. */
      for (var i = 0; i < els.length; i++) {
        els[i].setAttribute('placeholder', terms[0]);
      }
      return;
    }

    var typers = [];
    for (var j = 0; j < els.length; j++) {
      var tp = new Typer(els[j], terms);
      tp.bind();
      typers.push(tp);
    }

    document.addEventListener('visibilitychange', function () {
      for (var k = 0; k < typers.length; k++) {
        if (document.hidden) {
          typers[k].stop();
        } else {
          typers[k].at(typers[k].start, GAP_MS);
        }
      }
    });

    /* The overlay's input can be re-rendered by the live-search app, and
       some themes inject the drawer field late. Pick up newcomers without
       a MutationObserver on the whole document. */
    var sweeps = 0;
    var sweep = setInterval(function () {
      sweeps += 1;
      var fresh = fields();
      for (var m = 0; m < fresh.length; m++) {
        var t2 = new Typer(fresh[m], terms);
        t2.bind();
        typers.push(t2);
      }
      if (sweeps > 10) clearInterval(sweep);
    }, 1200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
